import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ApplicableViewEnum } from "./constants";
import { updateFormDetailsOnPaymentSuccess, useGetApplicableFormDetails } from "../dataloader";
import PurchaseOrderStatus from "shared/enums/PurchaseOrderStatus";
import { useParticipantDetails } from "../../../context/participantDetails";
import { MyExpRoute } from "../../../routes/types";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import { shouldRefreshPageOnError } from "shared/utils/network";
import { ApiPayForPurchaseOrderResponse } from "shared/types/ApiPayForPurchaseOrderResponse";
import { ApiRegistrationParticipantForParticipantResponse } from "shared/types/ApiRegistrationParticipantForParticipantResponse";
import { useQueryClient } from "react-query";
import QueryParamKey from "../../../../../shared/enums/QueryParamKey";
import ApiRegistrationParticipantTicketUpgradeFormResponse from "../../../../../shared/types/ApiRegistrationParticipantTicketUpgradeFormResponse";
import { isPaymentReceiptPendingForPO } from "apps/myExperience/utils";
import { refreshDashboardData } from "apps/myExperience/context/dashboard/dataloader";
import Reducer from "./reducer";
import { DispatchInterface, State } from "./types";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";

export default function useBuyTicketFormDetailsService() {
  const { formId } = useParams();
  const mountRef = React.useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigate } = useNavigateWrapper();
  const queryClient = useQueryClient();
  const { participantDetails } = useParticipantDetails();
  const [state, dispatcher] = useReducerWrapper<State, DispatchInterface>(Reducer);

  const navigateToFormsList = React.useCallback(
    (replace?: boolean) => {
      return navigate(`/${MyExpRoute.TICKET_CHANGE.path}/${MyExpRoute.TICKET_CHANGE.FORMS_LIST.path}`, {
        replace: replace
      });
    },
    [navigate]
  );

  const isOfflinePaymentConfirmationView = React.useMemo(
    () => searchParams.get(QueryParamKey.REQUEST_PENDING) === "true",
    [searchParams]
  );

  const navigateToApplicableView = React.useCallback(
    (purchaseOrder?: ApiPurchaseOrder, showCouponCode?: boolean, isPaymentReceiptPending?: boolean) => {
      if (purchaseOrder) {
        dispatcher.updatePurchaseOrder(purchaseOrder);
        const isCompleted = purchaseOrder.purchaseOrderStatus === PurchaseOrderStatus.COMPLETED;
        const shouldNavigateToConfirmation = isCompleted || !mountRef.current || isOfflinePaymentConfirmationView;

        if (shouldNavigateToConfirmation) {
          setSearchParams({
            [QueryParamKey.VIEW_TYPE]: ApplicableViewEnum.CONFIRMATION.toString(),
            ...((isPaymentReceiptPending || isOfflinePaymentConfirmationView) && {
              [QueryParamKey.REQUEST_PENDING]: "true"
            })
          });
          dispatcher.setActiveView(ApplicableViewEnum.CONFIRMATION);
        } else {
          dispatcher.setActiveView(ApplicableViewEnum.PAYMENT_DETAILS);
        }
      } else {
        dispatcher.setActiveView(showCouponCode ? ApplicableViewEnum.COUPON_CODE : ApplicableViewEnum.FORM_DETAILS);
      }
    },
    [dispatcher, setSearchParams, isOfflinePaymentConfirmationView]
  );

  const { data: formUpgradeTicketDetails, refetch: refetchTicketChangeDetails } = useGetApplicableFormDetails({
    registrationParticipantId: participantDetails?.registrationParticipantId,
    formId: formId,
    onSuccessHandler: data => {
      const isPaymentReceiptPending = isPaymentReceiptPendingForPO(
        data.registrationFormForTicketUpgrade.pendingPurchaseOrder
      );
      navigateToApplicableView(
        isPaymentReceiptPending
          ? data.registrationFormForTicketUpgrade.pendingPurchaseOrder
          : data.registrationParticipantForm.regPurchaseOrder,
        data.registrationParticipantForm.registrationForm.showCouponCode,
        isPaymentReceiptPending
      );
    },
    onErrorHandler: navigateToFormsList
  });

  const isInvoicePaymentOrOfflineApprovalPending = React.useMemo(
    () => isPaymentReceiptPendingForPO(state.purchaseOrder),
    [state.purchaseOrder]
  );

  const couponCodeApplySuccessHandler = React.useCallback(
    (data: { couponCode: string; responseData: ApiRegistrationParticipantTicketUpgradeFormResponse }) => {
      dispatcher.updateCouponApplyApplicableRate(data.responseData.registrationFormForTicketUpgrade);
      dispatcher.setActiveView(ApplicableViewEnum.FORM_DETAILS);
    },
    [dispatcher]
  );

  const invalidCouponCodeHandler = React.useCallback(() => {
    dispatcher.updateCouponApplyApplicableRate(undefined);
  }, [dispatcher]);

  const withoutCouponCodeHandler = React.useCallback(() => {
    dispatcher.updateCouponApplyApplicableRate(undefined);
    dispatcher.setActiveView(ApplicableViewEnum.FORM_DETAILS);
  }, [dispatcher]);

  const initiateFormSuccessHandler = React.useCallback(
    (data: ApiRegistrationParticipantForParticipantResponse) => {
      dispatcher.updateCouponApplyApplicableRate(undefined);
      navigateToApplicableView(data.regPurchaseOrder, data.registrationForm.showCouponCode);
    },
    [dispatcher, navigateToApplicableView]
  );

  const backClickHandler = React.useCallback(() => {
    switch (state.activeView) {
      case ApplicableViewEnum.COUPON_CODE:
        navigateToFormsList();
        break;
      case ApplicableViewEnum.FORM_DETAILS:
        if (
          !formUpgradeTicketDetails?.registrationParticipantForm?.regPurchaseOrder &&
          formUpgradeTicketDetails?.registrationParticipantForm?.registrationForm.showCouponCode
        ) {
          dispatcher.setActiveView(ApplicableViewEnum.COUPON_CODE);
        } else {
          navigateToFormsList();
        }
        break;
      case ApplicableViewEnum.PAYMENT_DETAILS:
        dispatcher.setActiveView(ApplicableViewEnum.FORM_DETAILS);
        break;
      default:
        navigateToFormsList();
    }
  }, [dispatcher, navigateToFormsList, formUpgradeTicketDetails, state.activeView]);

  const paymentSuccessHandler = React.useCallback(
    (paymentResponse: ApiPayForPurchaseOrderResponse) => {
      if (isPaymentReceiptPendingForPO(paymentResponse.purchaseOrder)) {
        refreshDashboardData(queryClient, participantDetails?.registrationParticipantId);
      }
      mountRef.current = false;
      updateFormDetailsOnPaymentSuccess({
        queryClient,
        registrationParticipantId: participantDetails?.registrationParticipantId,
        formId,
        paymentResponse: paymentResponse
      });
    },
    [queryClient, participantDetails?.registrationParticipantId, formId]
  );

  const paymentErrorHandler = React.useCallback(
    async err => {
      if (shouldRefreshPageOnError(err)) {
        refetchTicketChangeDetails();
      }
    },
    [refetchTicketChangeDetails]
  );

  const paymentMismatchHandler = React.useCallback(() => {
    refetchTicketChangeDetails();
  }, [refetchTicketChangeDetails]);

  const offlineTransactionUpdateSuccessHandler = React.useCallback(() => {
    setSearchParams({
      [QueryParamKey.VIEW_TYPE]: ApplicableViewEnum.CONFIRMATION.toString(),
      [QueryParamKey.REQUEST_PENDING]: "true"
    });
    dispatcher.setActiveView(ApplicableViewEnum.CONFIRMATION);
  }, [dispatcher, setSearchParams]);

  const offlineTransactionCancelSuccessHandler = React.useCallback(() => {
    navigateToFormsList(true);
    refreshDashboardData(queryClient, participantDetails?.registrationParticipantId);
  }, [queryClient, participantDetails?.registrationParticipantId, navigateToFormsList]);

  return {
    formUpgradeTicketDetails,
    state,
    initiateFormSuccessHandler,
    backClickHandler,
    participantDetails,
    paymentErrorHandler,
    paymentMismatchHandler,
    paymentSuccessHandler,
    couponCodeApplySuccessHandler,
    withoutCouponCodeHandler,
    invalidCouponCodeHandler,
    offlineTransactionUpdateSuccessHandler,
    offlineTransactionCancelSuccessHandler,
    isInvoicePaymentOrOfflineApprovalPending
  };
}
